import { AGENTS, type AgentId, type AgentRoom } from "./nav.js";

/** Trailing incomplete @query, e.g. "@Na" or "@" at end of draft. */
const ACTIVE_MENTION_RE = /(^|[\s([{])@([A-Za-z]*)$/;

export type ActiveMention = {
	/** Index of the `@` in the draft. */
	start: number;
	/** Query after `@` (may be empty). */
	query: string;
};

export type MentionSegment =
	| { kind: "text"; value: string }
	| { kind: "mention"; agent: AgentRoom; raw: string };

export function findActiveMention(draft: string): ActiveMention | null {
	const match = ACTIVE_MENTION_RE.exec(draft);
	if (!match) return null;
	const lead = match[1] ?? "";
	const query = match[2] ?? "";
	const atIndex = match.index + lead.length;
	return { start: atIndex, query };
}

export function agentsMatchingMention(query: string): AgentRoom[] {
	const q = query.trim().toLowerCase();
	if (!q) return [...AGENTS];
	return AGENTS.filter((agent) => {
		const name = agent.name.toLowerCase();
		const dept = agent.department.toLowerCase();
		const id = agent.id.toLowerCase();
		return (
			name.startsWith(q) ||
			dept.startsWith(q) ||
			id.startsWith(q) ||
			name.includes(q) ||
			dept.includes(q)
		);
	});
}

export function resolveMentionToken(token: string): AgentRoom | null {
	const q = token.trim().toLowerCase();
	if (!q) return null;
	const byName = AGENTS.find((a) => a.name.toLowerCase() === q);
	if (byName) return byName;
	const byDept = AGENTS.find(
		(a) => a.department.toLowerCase().replace(/\s+/g, "") === q,
	);
	if (byDept) return byDept;
	const byId = AGENTS.find((a) => a.id.toLowerCase() === q);
	return byId ?? null;
}

/** Remove an incomplete trailing `@query` after picking a chip. */
export function clearActiveMention(draft: string): string {
	const active = findActiveMention(draft);
	if (!active) return draft;
	return draft.slice(0, active.start).trimEnd();
}

/**
 * Pull completed @Name tokens out of the draft into agent chips.
 * Returns remaining draft text + newly found agents.
 */
export function extractCompletedMentions(draft: string): {
	text: string;
	agents: AgentRoom[];
} {
	const agents: AgentRoom[] = [];
	const seen = new Set<AgentId>();
	const text = draft.replace(
		/(^|[\s([{])@([A-Za-z][A-Za-z0-9_-]*)/g,
		(full, lead: string, token: string) => {
			const agent = resolveMentionToken(token);
			if (!agent || seen.has(agent.id)) return full;
			seen.add(agent.id);
			agents.push(agent);
			return lead;
		},
	);
	return { text: text.replace(/[ \t]{2,}/g, " ").replace(/^\s+/, ""), agents };
}

export function composeMessageWithMentions(
	text: string,
	agents: readonly AgentRoom[],
): string {
	const prefix = agents.map((a) => `@${a.name}`).join(" ");
	const body = text.trim();
	if (!prefix) return body;
	if (!body) return prefix;
	return `${prefix} ${body}`;
}

/** Split a message body into plain text + resolved mention segments for rich render. */
export function splitTextWithMentions(text: string): MentionSegment[] {
	if (!text) return [];
	const segments: MentionSegment[] = [];
	const re = /(^|[\s([{])@([A-Za-z][A-Za-z0-9_-]*)/g;
	let last = 0;
	let match: RegExpExecArray | null;
	while ((match = re.exec(text)) !== null) {
		const lead = match[1] ?? "";
		const token = match[2] ?? "";
		const atIndex = match.index + lead.length;
		const agent = resolveMentionToken(token);
		if (!agent) continue;
		if (atIndex > last) {
			const before = text.slice(last, match.index) + lead;
			if (before) segments.push({ kind: "text", value: before });
		} else if (lead) {
			segments.push({ kind: "text", value: lead });
		}
		const raw = `@${token}`;
		segments.push({ kind: "mention", agent, raw });
		last = atIndex + raw.length;
	}
	if (last < text.length) {
		segments.push({ kind: "text", value: text.slice(last) });
	}
	return segments.length > 0 ? segments : [{ kind: "text", value: text }];
}

export function parseMentionedAgentIds(text: string): AgentId[] {
	const found = new Set<AgentId>();
	for (const segment of splitTextWithMentions(text)) {
		if (segment.kind === "mention") found.add(segment.agent.id);
	}
	return [...found];
}
